import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Building2, Users, UserCog, Wallet, MapPin,
  ArrowRight, TrendingUp, Activity, ChevronRight, BarChart3,
  Plus, Eye
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DonneesDashboardPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchDashboard(); }, []);

  const fetchDashboard = async () => {
    try {
      const res = await fetch(`${API}/donnees/dashboard`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]" data-testid="donnees-dashboard-loading">
          <div className="animate-spin h-8 w-8 border-4 border-teal-500 border-t-transparent rounded-full" />
        </div>
      </Layout>
    );
  }

  const totals = data?.totals || {};
  const breakdowns = data?.breakdowns || {};
  const recent = data?.recent || {};

  const kpis = [
    { label: 'Entreprises', value: totals.entreprises, sub: `${totals.entreprises_actives} actives`, icon: Building2, color: 'violet', href: '/donnees/entreprises' },
    { label: 'Apprenants', value: totals.apprenants, sub: 'inscrits', icon: Users, color: 'blue', href: '/donnees/apprenants' },
    { label: 'Intervenants', value: totals.intervenants, sub: 'enregistrés', icon: UserCog, color: 'emerald', href: '/donnees/intervenants' },
    { label: 'Financeurs', value: totals.financeurs, sub: 'partenaires', icon: Wallet, color: 'amber', href: '/donnees/financeurs' },
    { label: 'Lieux', value: totals.lieux, sub: `${totals.capacite_totale} places`, icon: MapPin, color: 'rose', href: '/donnees/lieux' },
  ];

  const colorMap = {
    violet: { bg: 'bg-violet-50', text: 'text-violet-700', icon: 'bg-violet-100 text-violet-600', bar: 'bg-violet-500' },
    blue: { bg: 'bg-blue-50', text: 'text-blue-700', icon: 'bg-blue-100 text-blue-600', bar: 'bg-blue-500' },
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', icon: 'bg-emerald-100 text-emerald-600', bar: 'bg-emerald-500' },
    amber: { bg: 'bg-amber-50', text: 'text-amber-700', icon: 'bg-amber-100 text-amber-600', bar: 'bg-amber-500' },
    rose: { bg: 'bg-rose-50', text: 'text-rose-700', icon: 'bg-rose-100 text-rose-600', bar: 'bg-rose-500' },
  };

  const dispoColors = {
    'Disponible': 'bg-emerald-100 text-emerald-700',
    'Occupé': 'bg-amber-100 text-amber-700',
    'En congé': 'bg-slate-100 text-slate-600',
  };

  const maxBreakdown = (obj) => Math.max(...Object.values(obj || {}), 1);

  return (
    <Layout>
      <div className="space-y-8" data-testid="donnees-dashboard">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <span>Données</span>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-slate-600 font-medium">Tableau de bord</span>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-black text-slate-900" data-testid="dashboard-title">Données</h1>
            <p className="text-slate-500 mt-1">Vue d'ensemble de vos données de formation</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/donnees/apprenants/import')} data-testid="import-btn">
              <Plus className="h-4 w-4 mr-2" /> Importer
            </Button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="kpi-grid">
          {kpis.map((kpi) => {
            const c = colorMap[kpi.color];
            const Icon = kpi.icon;
            return (
              <button
                key={kpi.label}
                onClick={() => navigate(kpi.href)}
                className={`${c.bg} rounded-2xl p-5 text-left transition-all hover:scale-[1.02] hover:shadow-md group`}
                data-testid={`kpi-${kpi.label.toLowerCase()}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className={`p-2 rounded-xl ${c.icon}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <ArrowRight className={`h-4 w-4 ${c.text} opacity-0 group-hover:opacity-100 transition-opacity`} />
                </div>
                <div className={`text-3xl font-black ${c.text}`}>{kpi.value}</div>
                <div className="text-sm text-slate-500 mt-0.5">{kpi.label}</div>
                <div className="text-xs text-slate-400 mt-0.5">{kpi.sub}</div>
              </button>
            );
          })}
        </div>

        {/* Charts row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Entreprise types */}
          <Card className="border-0 shadow-sm" data-testid="chart-entreprise-types">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Building2 className="h-4 w-4 text-violet-500" />
                Types d'entreprises
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(breakdowns.entreprise_types || {}).map(([type, count]) => (
                  <div key={type}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-600 truncate">{type}</span>
                      <span className="font-semibold text-slate-800">{count}</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-violet-500 rounded-full transition-all duration-700"
                        style={{ width: `${(count / maxBreakdown(breakdowns.entreprise_types)) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
                {Object.keys(breakdowns.entreprise_types || {}).length === 0 && (
                  <p className="text-sm text-slate-400 text-center py-4">Aucune donnée</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Financeur types */}
          <Card className="border-0 shadow-sm" data-testid="chart-financeur-types">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Wallet className="h-4 w-4 text-amber-500" />
                Types de financeurs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(breakdowns.financeur_types || {}).map(([type, count]) => (
                  <div key={type}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-600 truncate">{type}</span>
                      <span className="font-semibold text-slate-800">{count}</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-amber-500 rounded-full transition-all duration-700"
                        style={{ width: `${(count / maxBreakdown(breakdowns.financeur_types)) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
                {Object.keys(breakdowns.financeur_types || {}).length === 0 && (
                  <p className="text-sm text-slate-400 text-center py-4">Aucune donnée</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Intervenant availability */}
          <Card className="border-0 shadow-sm" data-testid="chart-intervenant-dispo">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Activity className="h-4 w-4 text-emerald-500" />
                Disponibilité intervenants
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(breakdowns.intervenant_dispo || {}).map(([status, count]) => (
                  <div key={status} className="flex items-center gap-3">
                    <Badge className={`${dispoColors[status] || 'bg-slate-100 text-slate-600'} text-xs border-0 min-w-[90px] justify-center`}>
                      {status}
                    </Badge>
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-700 ${
                          status === 'Disponible' ? 'bg-emerald-500' : status === 'Occupé' ? 'bg-amber-500' : 'bg-slate-400'
                        }`}
                        style={{ width: `${totals.intervenants ? (count / totals.intervenants) * 100 : 0}%` }}
                      />
                    </div>
                    <span className="text-sm font-semibold text-slate-700 w-8 text-right">{count}</span>
                  </div>
                ))}
              </div>

              {/* Top cities */}
              {breakdowns.top_villes_apprenants?.length > 0 && (
                <div className="mt-6 pt-4 border-t border-slate-100">
                  <h4 className="text-sm font-medium text-slate-600 mb-3 flex items-center gap-2">
                    <BarChart3 className="h-3.5 w-3.5" /> Top villes (apprenants)
                  </h4>
                  <div className="space-y-2">
                    {breakdowns.top_villes_apprenants.map((v, i) => (
                      <div key={v.ville} className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">{i + 1}. {v.ville}</span>
                        <span className="font-medium text-slate-700">{v.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent entries */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Entreprises */}
          <Card className="border-0 shadow-sm" data-testid="recent-entreprises">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-violet-500" />
                  Entreprises récentes
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => navigate('/donnees/entreprises')} data-testid="view-all-entreprises">
                  <Eye className="h-3.5 w-3.5 mr-1" /> Tout voir
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {(recent.entreprises || []).map((e) => (
                  <button
                    key={e.entreprise_id}
                    onClick={() => navigate(`/donnees/entreprises/${e.entreprise_id}`)}
                    className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-colors text-left group"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{e.name}</p>
                      {e.type && <p className="text-xs text-slate-400">{e.type}</p>}
                    </div>
                    <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-slate-500 shrink-0" />
                  </button>
                ))}
                {(!recent.entreprises || recent.entreprises.length === 0) && (
                  <p className="text-sm text-slate-400 text-center py-3">Aucune entreprise</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Recent Apprenants */}
          <Card className="border-0 shadow-sm" data-testid="recent-apprenants">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Users className="h-4 w-4 text-blue-500" />
                  Apprenants récents
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => navigate('/donnees/apprenants')} data-testid="view-all-apprenants">
                  <Eye className="h-3.5 w-3.5 mr-1" /> Tout voir
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {(recent.apprenants || []).map((a) => (
                  <button
                    key={a.apprenant_id}
                    onClick={() => navigate(`/donnees/apprenants/${a.apprenant_id}`)}
                    className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-colors text-left group"
                  >
                    <p className="text-sm font-medium text-slate-800 truncate">{a.prenom} {a.nom}</p>
                    <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-slate-500 shrink-0" />
                  </button>
                ))}
                {(!recent.apprenants || recent.apprenants.length === 0) && (
                  <p className="text-sm text-slate-400 text-center py-3">Aucun apprenant</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Recent Intervenants */}
          <Card className="border-0 shadow-sm" data-testid="recent-intervenants">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <UserCog className="h-4 w-4 text-emerald-500" />
                  Intervenants récents
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => navigate('/donnees/intervenants')} data-testid="view-all-intervenants">
                  <Eye className="h-3.5 w-3.5 mr-1" /> Tout voir
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {(recent.intervenants || []).map((i) => (
                  <button
                    key={i.intervenant_id}
                    onClick={() => navigate(`/donnees/intervenants/${i.intervenant_id}`)}
                    className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-colors text-left group"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{i.prenom} {i.nom}</p>
                      {i.disponibilite && (
                        <Badge className={`${dispoColors[i.disponibilite] || 'bg-slate-100 text-slate-600'} text-[10px] border-0`}>
                          {i.disponibilite}
                        </Badge>
                      )}
                    </div>
                    <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-slate-500 shrink-0" />
                  </button>
                ))}
                {(!recent.intervenants || recent.intervenants.length === 0) && (
                  <p className="text-sm text-slate-400 text-center py-3">Aucun intervenant</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card className="border-0 shadow-sm" data-testid="quick-actions">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-teal-500" />
              Actions rapides
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {[
                { label: 'Nouvelle entreprise', href: '/donnees/entreprises/nouveau', icon: Building2, color: 'violet' },
                { label: 'Nouvel apprenant', href: '/donnees/apprenants/nouveau', icon: Users, color: 'blue' },
                { label: 'Nouvel intervenant', href: '/donnees/intervenants/nouveau', icon: UserCog, color: 'emerald' },
                { label: 'Nouveau financeur', href: '/donnees/financeurs/nouveau', icon: Wallet, color: 'amber' },
                { label: 'Nouveau lieu', href: '/donnees/lieux/nouveau', icon: MapPin, color: 'rose' },
              ].map((action) => {
                const Icon = action.icon;
                return (
                  <Button
                    key={action.label}
                    variant="outline"
                    className="h-auto py-4 flex-col gap-2 hover:border-slate-300 hover:bg-slate-50"
                    onClick={() => navigate(action.href)}
                    data-testid={`action-${action.color}`}
                  >
                    <Icon className={`h-5 w-5 text-${action.color}-500`} />
                    <span className="text-xs text-slate-600 text-center leading-tight">{action.label}</span>
                  </Button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default DonneesDashboardPage;
